import { useMemo, useState } from 'react';
import { NavIcon, useBrandTokens } from '../../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import {
  qVitalsByEncounterId,
  qActiveVitalDefinitions,
  type PatientVitalEntryWithDefinitionRow,
  type VitalDefinitionRow,
} from '@helios/data-access';
import { RecordVitalsDrawer } from '../../../shell/RecordVitalsDrawer';

// ─── Helpers ───

function formatVitalValue(row: PatientVitalEntryWithDefinitionRow): string {
  switch (row.value_type) {
    case 'number':
    case 'integer':
      return row.value_number != null ? String(row.value_number) : '--';
    case 'text':
      return row.value_text ?? '--';
    case 'date':
      return row.value_date
        ? new Date(row.value_date).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })
        : '--';
    case 'boolean':
      return row.value_boolean === 1 ? 'Yes' : row.value_boolean === 0 ? 'No' : '--';
    default:
      return '--';
  }
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ─── Props ───

interface EncounterVitalsTabProps {
  patientId: string;
  encounterId: string;
  /**
   * Suppress the section's own title/description.
   *
   * Guided Notes already draws a "Vitals" card header, so repeating it inside
   * would show the word twice. The Record Vitals action is deliberately NOT
   * hidden — it is the only way to enter the first reading from Guided Notes.
   */
  hideHeader?: boolean;
}

// ─── Component ───

export function EncounterVitalsTab({
  patientId,
  encounterId,
  hideHeader = false,
}: EncounterVitalsTabProps) {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { db } = usePowerSyncDb();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data: vitalRows = [] } = usePowerSyncQuery<PatientVitalEntryWithDefinitionRow>(
    qVitalsByEncounterId(encounterId),
  );

  const { data: vitalDefinitions = [] } =
    usePowerSyncQuery<VitalDefinitionRow>(qActiveVitalDefinitions());

  // Group by recorded_at session
  const sessions = useMemo(() => {
    const map = new Map<string, PatientVitalEntryWithDefinitionRow[]>();
    for (const row of vitalRows) {
      const key = row.recorded_at;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(row);
    }
    return Array.from(map.entries()).map(([recordedAt, entries]) => ({
      recordedAt,
      primary: entries.filter((e) => (e.category ?? 'primary') !== 'additional'),
      additional: entries.filter((e) => e.category === 'additional'),
    }));
  }, [vitalRows]);

  const latestRecordedAt = sessions[0]?.recordedAt ?? null;

  return (
    <div style={{ padding: 'var(--sp-5)' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          // With the title hidden the action is the row's only child, so it
          // right-aligns instead of drifting to the left edge.
          justifyContent: hideHeader ? 'flex-end' : 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--sp-5)',
        }}
      >
        {!hideHeader && (
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              Vitals
            </h2>
            <p
              style={{
                margin: '4px 0 0',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
              }}
            >
              Vitals recorded during this encounter
            </p>
          </div>
        )}
        <button
          onClick={() => setDrawerOpen(true)}
          style={{
            height: 36,
            padding: '0 16px',
            borderRadius: 'var(--radius-md)',
            border: 'none',
            background: primary,
            color: '#fff',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <NavIcon name="plus" /> Record Vitals
        </button>
      </div>

      {/* Empty state */}
      {sessions.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            padding: 'var(--sp-8)',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-sm)',
            border: '2px dashed var(--border)',
            borderRadius: 'var(--radius-lg)',
          }}
        >
          No vitals recorded for this encounter yet.
        </div>
      )}

      {/* Sessions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
        {sessions.map((session, idx) => (
          <div
            key={session.recordedAt}
            style={{
              background: 'var(--bg-surface-2, #f9fafb)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--sp-5)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 'var(--sp-4)',
              }}
            >
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                }}
              >
                {formatDateTime(session.recordedAt)}
              </span>
              {idx === 0 && (
                <span
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    padding: '2px 8px',
                    borderRadius: 'var(--radius-xs)',
                    background: 'var(--color-success-bg)',
                    color: 'var(--color-success-text)',
                    fontWeight: 'var(--font-weight-medium)',
                  }}
                >
                  Latest
                </span>
              )}
            </div>

            {/* Primary vitals */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: 'var(--sp-3)',
              }}
            >
              {session.primary.map((v) => (
                <div
                  key={v.id}
                  style={{
                    background: 'var(--bg-surface)',
                    borderRadius: 'var(--radius-md)',
                    padding: 'var(--sp-3)',
                    border: '1px solid var(--border)',
                  }}
                >
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--text-muted)',
                      marginBottom: 4,
                    }}
                  >
                    {v.name}
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-lg)',
                      fontWeight: 'var(--font-weight-semi)',
                      color: 'var(--text)',
                    }}
                  >
                    {formatVitalValue(v)}
                    {v.unit && (
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--text-muted)',
                          marginLeft: 4,
                        }}
                      >
                        {v.unit}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Additional vitals */}
            {session.additional.length > 0 && (
              <>
                <div
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                    marginTop: 'var(--sp-4)',
                    marginBottom: 'var(--sp-2)',
                    fontWeight: 'var(--font-weight-medium)',
                  }}
                >
                  Additional
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                    gap: 'var(--sp-3)',
                  }}
                >
                  {session.additional.map((v) => (
                    <div
                      key={v.id}
                      style={{
                        background: 'var(--bg-surface)',
                        borderRadius: 'var(--radius-md)',
                        padding: 'var(--sp-3)',
                        border: '1px solid var(--border)',
                      }}
                    >
                      <div
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--text-muted)',
                          marginBottom: 4,
                        }}
                      >
                        {v.name}
                      </div>
                      <div
                        style={{
                          fontSize: 'var(--font-size-lg)',
                          fontWeight: 'var(--font-weight-semi)',
                          color: 'var(--text)',
                        }}
                      >
                        {formatVitalValue(v)}
                        {v.unit && (
                          <span
                            style={{
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--text-muted)',
                              marginLeft: 4,
                            }}
                          >
                            {v.unit}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Record Vitals Drawer */}
      <RecordVitalsDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        brandTokens={brandTokens}
        patientId={patientId}
        vitalDefinitions={vitalDefinitions}
        latestRecordedAt={latestRecordedAt}
        db={db}
        encounterId={encounterId}
        source="encounter"
        mode="encounter"
      />
    </div>
  );
}
